import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Image, Spin } from "antd";
import { DownOutlined, LikeFilled, LikeOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import { observer } from "mobx-react-lite";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { ViewpointFeedApi } from "./ViewpointFeedApi";
import type { ViewpointFeedCategoryItem, ViewpointFeedPost } from "./ViewpointFeedTypes";
import "./ViewpointFeedPage.css";

const PAGE_SIZE = 8;
const POLL_MS = 5000;
/** 分类标签与自选股分组一致：超过该数量时收入「更多分类」 */
const FEED_CATEGORY_INLINE_MAX = 6;

function normalizeFeedPost(p: ViewpointFeedPost): ViewpointFeedPost {
  return {
    ...p,
    likeCount: p.likeCount ?? 0,
    collectCount: p.collectCount ?? 0,
  };
}

function ViewpointFeedEngagementActions({
  row,
  onToggleLike,
  onToggleCollect,
}: {
  row: ViewpointFeedPost;
  onToggleLike: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
  onToggleCollect: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
}) {
  const liked = Boolean(row.likedByMe);
  const collected = Boolean(row.collectedByMe);
  const likeCount = row.likeCount ?? 0;
  const collectCount = row.collectCount ?? 0;
  return (
    <div className="lf-vp-card-actions" aria-label="点赞与收藏">
      <button
        type="button"
        className="lf-vp-action-btn"
        onClick={(e) => void onToggleLike(e, row)}
        aria-label={liked ? "取消点赞" : "点赞"}
      >
        {liked ? (
          <LikeFilled className="lf-vp-action-ic lf-vp-action-ic--on" />
        ) : (
          <LikeOutlined className="lf-vp-action-ic" />
        )}
        <span className="lf-vp-action-num">{likeCount}</span>
      </button>
      <button
        type="button"
        className="lf-vp-action-btn"
        onClick={(e) => void onToggleCollect(e, row)}
        aria-label={collected ? "取消收藏" : "收藏"}
      >
        {collected ? (
          <StarFilled className="lf-vp-action-ic lf-vp-action-ic--collect-on" />
        ) : (
          <StarOutlined className="lf-vp-action-ic" />
        )}
        <span className="lf-vp-action-num">{collectCount}</span>
      </button>
    </div>
  );
}

function displayPublishedCompact(v: unknown): string {
  if (v == null || v === "") return "";
  if (v instanceof Date) return dayjs(v).format("M月D日 HH:mm");
  if (typeof v === "string") {
    const t = v.trim();
    const d = dayjs(t);
    return d.isValid() ? d.format("M月D日 HH:mm") : t;
  }
  return String(v).trim();
}

function nicknameOf(row: ViewpointFeedPost): string {
  const n = row.publisherNickname?.trim();
  if (n) return n;
  return row.publisherLabel?.trim() || "用户";
}

function refreshedAtLabel(t: Date | null): string {
  if (!t) return "—";
  return dayjs(t).format("M月D日 HH:mm");
}

type FeedListFilter = "all" | "liked" | "collected";

function importanceClass(imp: string): string {
  if (imp === "urgent") return "lf-vp-imp lf-vp-imp--urgent";
  if (imp === "important") return "lf-vp-imp lf-vp-imp--important";
  return "lf-vp-imp lf-vp-imp--normal";
}

function importanceLabel(imp: string): string {
  if (imp === "urgent") return "精选";
  if (imp === "important") return "重要";
  return "";
}

export interface ViewpointFeedPageProps {
  spaceId: string;
  spaceProductId: string;
  spaceDisplayName: string;
}

const ViewpointFeedPage: React.FC<ViewpointFeedPageProps> = observer(
  ({ spaceId, spaceProductId, spaceDisplayName: _spaceDisplayName }) => {
    const [items, setItems] = useState<ViewpointFeedPost[]>([]);
    const [hasMoreOlder, setHasMoreOlder] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [feedFilter, setFeedFilter] = useState<FeedListFilter>("all");
    const [feedCategories, setFeedCategories] = useState<ViewpointFeedCategoryItem[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
    const [categoryMoreOpen, setCategoryMoreOpen] = useState(false);
    const itemsRef = useRef<ViewpointFeedPost[]>([]);
    itemsRef.current = items;
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      setSelectedCategoryId(null);
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      void (async () => {
        const res = await ViewpointFeedApi.listViewpointCategories({ spaceId, spaceProductId });
        if (typeof res === "string") {
          setFeedCategories([]);
          return;
        }
        setFeedCategories(Array.isArray(res.list) ? res.list : []);
      })();
    }, [spaceId, spaceProductId]);

    const reloadFirstPage = useCallback(async () => {
      setLoading(true);
      try {
        const res = await ViewpointFeedApi.getOldViewpoints({
          spaceId,
          spaceProductId,
          limit: PAGE_SIZE,
          ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
        });
        if (typeof res === "string") {
          setItems([]);
          setHasMoreOlder(false);
          return;
        }
        setHasMoreOlder(res.hasMore);
        setItems(res.list.map(normalizeFeedPost));
        setRefreshedAt(new Date());
      } finally {
        setLoading(false);
      }
    }, [spaceId, spaceProductId, selectedCategoryId]);

    const displayItems = useMemo(() => {
      if (feedFilter === "liked") {
        return items.filter((x) => Boolean(x.likedByMe));
      }
      if (feedFilter === "collected") {
        return items.filter((x) => Boolean(x.collectedByMe));
      }
      return items;
    }, [items, feedFilter]);

    useEffect(() => {
      void reloadFirstPage();
    }, [reloadFirstPage]);

    useEffect(() => {
      const el = sentinelRef.current;
      if (!el) return undefined;
      const ob = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry?.isIntersecting || loading || loadingMore) return;
          if (!hasMoreOlder) return;
          const last = itemsRef.current[itemsRef.current.length - 1];
          if (!last) return;
          setLoadingMore(true);
          void (async () => {
            try {
              const res = await ViewpointFeedApi.getOldViewpoints({
                spaceId,
                spaceProductId,
                limit: PAGE_SIZE,
                cursorPublishedAt: last.publishedAt,
                cursorId: last.id,
                ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
              });
              if (typeof res === "string") {
                return;
              }
              setHasMoreOlder(res.hasMore);
              setItems((prev) => [...prev, ...res.list.map(normalizeFeedPost)]);
            } finally {
              setLoadingMore(false);
            }
          })();
        },
        { root: null, rootMargin: "120px", threshold: 0 }
      );
      ob.observe(el);
      return () => ob.disconnect();
    }, [
      loading,
      loadingMore,
      hasMoreOlder,
      spaceId,
      spaceProductId,
      selectedCategoryId,
      items.length,
    ]);

    useEffect(() => {
      const id = window.setInterval(() => {
        const top = itemsRef.current[0];
        if (!top) return;
        void (async () => {
          const res = await ViewpointFeedApi.getLatestViewpoints({
            spaceId,
            spaceProductId,
            limit: PAGE_SIZE,
            cursorPublishedAt: top.publishedAt,
            cursorId: top.id,
            ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
          });
          if (typeof res === "string" || !res.list.length) return;
          setItems((prev) => {
            const seen = new Set(prev.map((p) => p.id));
            const merged = res.list.map(normalizeFeedPost).filter((p) => !seen.has(p.id));
            if (!merged.length) return prev;
            return [...merged, ...prev];
          });
        })();
      }, POLL_MS);
      return () => window.clearInterval(id);
    }, [spaceId, spaceProductId, selectedCategoryId]);

    const patchEngagement = useCallback(
      (id: string, liked: boolean, collected: boolean) => {
        setItems((prev) =>
          prev.map((r) => {
            if (r.id !== id) return r;
            let likeCount = r.likeCount ?? 0;
            let collectCount = r.collectCount ?? 0;
            const wasLiked = Boolean(r.likedByMe);
            const wasColl = Boolean(r.collectedByMe);
            if (liked !== wasLiked) {
              likeCount += liked ? 1 : -1;
            }
            if (collected !== wasColl) {
              collectCount += collected ? 1 : -1;
            }
            return {
              ...r,
              likedByMe: liked,
              collectedByMe: collected,
              likeCount: Math.max(0, likeCount),
              collectCount: Math.max(0, collectCount),
            };
          })
        );
      },
      []
    );

    const onToggleLike = useCallback(
      async (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        const res = await ViewpointFeedApi.toggleLike({
          spaceId,
          spaceProductId,
          viewpointId: row.id,
        });
        if (typeof res === "string") {
          return;
        }
        patchEngagement(row.id, res.liked, res.collected);
      },
      [spaceId, spaceProductId, patchEngagement]
    );

    const onToggleCollect = useCallback(
      async (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        const res = await ViewpointFeedApi.toggleCollect({
          spaceId,
          spaceProductId,
          viewpointId: row.id,
        });
        if (typeof res === "string") {
          return;
        }
        patchEngagement(row.id, res.liked, res.collected);
      },
      [spaceId, spaceProductId, patchEngagement]
    );

    const showEnd = !loading && items.length > 0 && !hasMoreOlder;
    const showFilteredEmpty =
      !loading && items.length > 0 && displayItems.length === 0;

    const onClickRefresh = useCallback(() => {
      void reloadFirstPage();
    }, [reloadFirstPage]);

    const onPickFeedCategory = useCallback(
      (categoryId: string) => {
        const id = categoryId.trim();
        if (!id) return;
        const nextId = selectedCategoryId === id ? null : id;
        setSelectedCategoryId(nextId);
        if (nextId !== null) {
          setFeedFilter("all");
        }
        setCategoryMoreOpen(false);
      },
      [selectedCategoryId]
    );

    const onSelectFeedCategory = useCallback((categoryId: string) => {
      const id = categoryId.trim();
      if (!id) return;
      setSelectedCategoryId(id);
      setFeedFilter("all");
    }, []);

    const validFeedCategories = useMemo(
      () =>
        feedCategories.filter((c) => {
          const nm = c.name?.trim();
          const sid = c.id?.trim();
          return Boolean(nm && sid);
        }),
      [feedCategories]
    );

    const inlineFeedCategories = useMemo(
      () => validFeedCategories.slice(0, FEED_CATEGORY_INLINE_MAX),
      [validFeedCategories]
    );

    const overflowFeedCategories = useMemo(() => {
      if (validFeedCategories.length <= FEED_CATEGORY_INLINE_MAX) return [];
      return validFeedCategories.slice(FEED_CATEGORY_INLINE_MAX);
    }, [validFeedCategories]);

    const selectedFeedCategoryIndex = useMemo(() => {
      if (!selectedCategoryId) return -1;
      const id = selectedCategoryId.trim();
      return validFeedCategories.findIndex((c) => c.id.trim() === id);
    }, [selectedCategoryId, validFeedCategories]);

    const feedCategoryMoreActive =
      overflowFeedCategories.length > 0 && selectedFeedCategoryIndex >= FEED_CATEGORY_INLINE_MAX;

    const feedCategoryMoreTriggerLabel = useMemo(() => {
      if (overflowFeedCategories.length === 0) return "更多分类";
      if (!feedCategoryMoreActive || selectedFeedCategoryIndex < 0) return "更多分类";
      return (
        validFeedCategories[selectedFeedCategoryIndex]?.name?.trim() || "更多分类"
      );
    }, [
      overflowFeedCategories.length,
      feedCategoryMoreActive,
      selectedFeedCategoryIndex,
      validFeedCategories,
    ]);

    return (
      <div className="lf-vp-feed">
        <div className="lf-vp-feed__inner">
          <div className="lf-vp-feed__toolbar" aria-label="列表筛选与刷新">
            <div className="lf-vp-feed__filters" role="tablist" aria-label="观点范围">
              {(
                [
                  ["all", "全部"],
                  ["liked", "我点赞的"],
                  ["collected", "我收藏的"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={feedFilter === key}
                  className={
                    feedFilter === key
                      ? "lf-vp-feed__filter lf-vp-feed__filter--on"
                      : "lf-vp-feed__filter"
                  }
                  onClick={() => setFeedFilter(key)}
                >
                  {label}
                </button>
              ))}
              {inlineFeedCategories.map((c) => {
                const nm = c.name!.trim();
                const on = selectedCategoryId === c.id.trim();
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    className={
                      on ? "lf-vp-feed__filter lf-vp-feed__filter--on" : "lf-vp-feed__filter"
                    }
                    onClick={() => onPickFeedCategory(c.id)}
                  >
                    {nm}
                  </button>
                );
              })}
              {overflowFeedCategories.length > 0 ? (
                <Dropdown
                  trigger={["click"]}
                  open={categoryMoreOpen}
                  onOpenChange={setCategoryMoreOpen}
                  popupRender={() => (
                    <div className="lf-vp-feed__filter-more-panel" role="listbox">
                      {overflowFeedCategories.map((c) => {
                        const nm = c.name!.trim();
                        const on = selectedCategoryId === c.id.trim();
                        return (
                          <button
                            key={c.id}
                            type="button"
                            role="option"
                            className={
                              on
                                ? "lf-vp-feed__filter-more-cell lf-vp-feed__filter-more-cell--on"
                                : "lf-vp-feed__filter-more-cell"
                            }
                            onClick={() => onPickFeedCategory(c.id)}
                            title={nm}
                          >
                            <span className="lf-vp-feed__filter-more-cell-text">{nm}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  overlayStyle={{ padding: 0, background: "transparent", boxShadow: "none" }}
                >
                  <span className="lf-vp-feed__filter-more-anchor">
                    <button
                      type="button"
                      className={
                        feedCategoryMoreActive
                          ? "lf-vp-feed__filter lf-vp-feed__filter--on lf-vp-feed__filter-more-trigger"
                          : "lf-vp-feed__filter lf-vp-feed__filter-more-trigger"
                      }
                      aria-expanded={categoryMoreOpen}
                      aria-haspopup="listbox"
                    >
                      <span className="lf-vp-feed__filter-more-label">
                        {feedCategoryMoreTriggerLabel}
                      </span>
                      <DownOutlined className="lf-vp-feed__filter-more-caret" />
                    </button>
                  </span>
                </Dropdown>
              ) : null}
            </div>
            <button
              type="button"
              className="lf-vp-feed__refresh"
              onClick={onClickRefresh}
              disabled={loading}
              aria-label="立即刷新列表"
            >
              更新于 {refreshedAtLabel(refreshedAt)}
            </button>
          </div>
          <Spin spinning={loading}>
            <div className="lf-vp-feed__waterfall">
              {showFilteredEmpty ? (
                <div className="lf-vp-feed__empty-filter">当前筛选下暂无内容</div>
              ) : null}
              {displayItems.map((row) => {
                const nick = nicknameOf(row);
                const titleText =
                  row.titleMode && row.title?.trim() ? row.title.trim() : null;
                const categoryName = row.categoryName?.trim() || "";
                const categoryId = row.categoryId?.trim() || "";
                const showImportance =
                  row.importance === "important" || row.importance === "urgent";
                return (
                  <article key={row.id} className="lf-vp-card">
                    <div className="lf-vp-card__head">
                      <EasyAvatar
                        shape="square"
                        squareRadius={4}
                        size={30}
                        url={row.publisherAvatarUrl?.trim() || undefined}
                        word={avatarDisplayWord(nick, row.publisherLabel)}
                        alt=""
                        className="lf-vp-card__avatar"
                      />
                      <div className="lf-vp-card__main">
                        <div className="lf-vp-card__user-line">
                          <span className="lf-vp-card__nick">{nick}</span>
                          <span className="lf-vp-card__time-inline">
                            {displayPublishedCompact(row.publishedAt)}
                          </span>
                        </div>
                        {titleText ? (
                          <div className="lf-vp-card__title-text">{titleText}</div>
                        ) : null}
                        <div className="lf-vp-card__body">
                          {showImportance ? (
                            <span className={importanceClass(row.importance)}>
                              {importanceLabel(row.importance)}
                            </span>
                          ) : null}
                          {row.content}
                          {categoryName && categoryId ? (
                            <>
                              {" "}
                              <button
                                type="button"
                                className="lf-vp-card__hash-tag"
                                onClick={() => onSelectFeedCategory(categoryId)}
                              >
                                #{categoryName}
                              </button>
                            </>
                          ) : null}
                        </div>
                        {row.imageUrls?.length ? (
                          <div className="lf-vp-card__images">
                            <Image.PreviewGroup preview={{ destroyOnClose: true }}>
                              <div className="lf-vp-card__images-inner">
                                {row.imageUrls.map((u, idx) => (
                                  <div key={`${idx}-${u}`} className="lf-vp-card__image-cell">
                                    <Image src={u} alt="" className="lf-vp-card__image" />
                                  </div>
                                ))}
                              </div>
                            </Image.PreviewGroup>
                          </div>
                        ) : null}
                      </div>
                      <ViewpointFeedEngagementActions
                        row={row}
                        onToggleLike={onToggleLike}
                        onToggleCollect={onToggleCollect}
                      />
                    </div>
                  </article>
                );
              })}
              <div ref={sentinelRef} className="lf-vp-feed__sentinel" aria-hidden />
              {loadingMore ? (
                <div className="lf-vp-feed__loading-more">
                  <Spin size="small" /> 加载中…
                </div>
              ) : null}
              {showEnd ? <div className="lf-vp-feed__end">已经到底啦</div> : null}
            </div>
          </Spin>
        </div>
      </div>
    );
  }
);

export default ViewpointFeedPage;
